import { ArrowDownLeft, ArrowUpRight, HandCoins, History, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { useAuth } from '@/lib/auth/AuthProvider'
import { STAFF_ADVANCE_PERMS as P, useAdvanceAction, useAdvanceBalances, useAdvanceMoves, useAdvanceStaff, type AdvanceBalance, type AdvanceInput } from '../crm/expenses'
import { fmtDate, fmtMoney } from '../shared/format'
import { useAccounts } from './api'
import { Field } from './components/FormBits'
import { toastError } from './ui'

const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' })
const MOVE_TONE = { give: 'info', expense: 'neutral', return: 'success' } as const

/**
 * Personel iş avansları (195 İş Avansları): kasadan/bankadan verilen avans, "iş avansından" ödenen müşteri giderleri onayda
 * mahsup edilir; artan avans kasaya/bankaya iade alınır. Bakiye = verilen − gidere mahsup − iade.
 */
export function StaffAdvancesPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const q = useAdvanceBalances()
  const [action, setAction] = useState<{ kind: 'give' | 'return'; row?: AdvanceBalance } | null>(null)
  const [history, setHistory] = useState<AdvanceBalance | null>(null)
  const rows = q.data ?? []
  const total = rows.reduce((n, r) => n + Number(r.balance), 0)
  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.staffAdvances.title')}
        description={t('finance.staffAdvances.subtitle')}
        actions={
          can(P.give) ? (
            <Button onClick={() => setAction({ kind: 'give' })}>
              <Plus />
              {t('finance.staffAdvances.give')}
            </Button>
          ) : null
        }
      />
      <section className="rounded-xl border bg-card shadow-card">
        {q.isError ? (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        ) : q.isLoading ? (
          <Skeleton className="m-4 h-40" />
        ) : !rows.length ? (
          <EmptyState icon={HandCoins} title={t('finance.staffAdvances.empty')} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <TR>
                  <TH>{t('finance.staffAdvances.staff')}</TH>
                  <TH className="text-right">{t('finance.staffAdvances.given')}</TH>
                  <TH className="text-right">{t('finance.staffAdvances.spent')}</TH>
                  <TH className="text-right">{t('finance.staffAdvances.returned')}</TH>
                  <TH className="text-right">{t('finance.staffAdvances.balance')}</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {rows.map((r) => (
                  <TR key={r.userId}>
                    <TD className="font-medium">{r.userName}</TD>
                    <TD className="text-right tabular-nums">{fmtMoney(r.given)}</TD>
                    <TD className="text-right tabular-nums">{fmtMoney(r.spent)}</TD>
                    <TD className="text-right tabular-nums">{fmtMoney(r.returned)}</TD>
                    <TD className="text-right font-semibold tabular-nums">{fmtMoney(r.balance)}</TD>
                    <TD className="whitespace-nowrap text-right">
                      <Button size="sm" variant="ghost" onClick={() => setHistory(r)}>
                        <History />
                        {t('finance.staffAdvances.moves')}
                      </Button>
                      {can(P.give) ? (
                        <Button size="sm" variant="ghost" onClick={() => setAction({ kind: 'give', row: r })}>
                          <ArrowUpRight />
                          {t('finance.staffAdvances.giveShort')}
                        </Button>
                      ) : null}
                      {can(P.ret) && Number(r.balance) > 0 ? (
                        <Button size="sm" variant="ghost" onClick={() => setAction({ kind: 'return', row: r })}>
                          <ArrowDownLeft />
                          {t('finance.staffAdvances.returnShort')}
                        </Button>
                      ) : null}
                    </TD>
                  </TR>
                ))}
                <TR className="border-t-2 font-semibold">
                  <TD colSpan={4}>{t('finance.staffAdvances.openTotal')}</TD>
                  <TD className="text-right tabular-nums">{fmtMoney(total.toFixed(2))}</TD>
                  <TD />
                </TR>
              </TBody>
            </Table>
          </div>
        )}
      </section>
      {action ? <AdvanceModal kind={action.kind} row={action.row} onClose={() => setAction(null)} /> : null}
      {history ? <MovesModal row={history} onClose={() => setHistory(null)} /> : null}
    </>
  )
}

function AdvanceModal({ kind, row, onClose }: { kind: 'give' | 'return'; row?: AdvanceBalance; onClose: () => void }) {
  const { t } = useTranslation()
  const accounts = useAccounts()
  const staff = useAdvanceStaff(kind === 'give' && !row)
  const act = useAdvanceAction()
  const [v, setV] = useState<AdvanceInput>({ userId: row?.userId ?? '', accountId: '', amount: kind === 'return' ? (row?.balance ?? '') : '', date: today(), description: '' })
  const set = (p: Partial<AdvanceInput>) => setV((x) => ({ ...x, ...p }))
  const options = (accounts.data ?? []).filter((a) => a.active && (a.type === 'cash' || a.type === 'bank') && (!a.currency || a.currency === 'TRY'))
  const amount = Number(v.amount.replace(',', '.'))
  const overReturn = kind === 'return' && amount > Number(row?.balance ?? 0)
  const ok = !!v.userId && !!v.accountId && amount > 0 && !!v.date && !overReturn
  return (
    <Modal
      open
      size="md"
      onOpenChange={(o) => !o && onClose()}
      icon={kind === 'give' ? <ArrowUpRight /> : <ArrowDownLeft />}
      title={t(kind === 'give' ? 'finance.staffAdvances.giveTitle' : 'finance.staffAdvances.returnTitle', { name: row?.userName ?? '' })}
      description={t(kind === 'give' ? 'finance.staffAdvances.giveHint' : 'finance.staffAdvances.returnHint', { amount: fmtMoney(row?.balance ?? '0') })}
      footer={
        <>
          <Button variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!ok || act.isPending}
            onClick={() =>
              act.mutate(
                { kind, input: v },
                {
                  onSuccess: () => {
                    toast.success(t(kind === 'give' ? 'finance.staffAdvances.givenToast' : 'finance.staffAdvances.returnedToast'))
                    onClose()
                  },
                  onError: toastError,
                },
              )
            }
          >
            {t(kind === 'give' ? 'finance.staffAdvances.giveShort' : 'finance.staffAdvances.returnShort')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {!row ? (
          <Field id="sa-user" label={t('finance.staffAdvances.staff')}>
            <Select
              id="sa-user"
              searchable
              value={v.userId}
              onChange={(userId) => set({ userId })}
              options={[{ value: '', label: t('common.select') }, ...(staff.data ?? []).map((u) => ({ value: u.id, label: u.name }))]}
            />
          </Field>
        ) : null}
        <Field id="sa-acc" label={t(kind === 'give' ? 'finance.staffAdvances.fromAccount' : 'finance.staffAdvances.toAccount')}>
          <Select id="sa-acc" value={v.accountId} onChange={(accountId) => set({ accountId })} options={[{ value: '', label: t('common.select') }, ...options.map((a) => ({ value: a.id, label: `${a.name} · ${fmtMoney(a.balance)}` }))]} />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field id="sa-amount" label={t('finance.staffAdvances.amount')}>
            <NumberInput id="sa-amount" value={v.amount} onChange={(amount) => set({ amount })} minDecimalsOnBlur={2} suffix="₺" />
          </Field>
          <Field id="sa-date" label={t('finance.staffAdvances.date')}>
            <DateField id="sa-date" value={v.date} max={today()} onChange={(date) => set({ date })} clearable={false} />
          </Field>
        </div>
        {overReturn ? <p className="text-xs text-destructive">{t('finance.staffAdvances.overReturn')}</p> : null}
        <Field id="sa-desc" label={t('finance.staffAdvances.description')}>
          <Input id="sa-desc" value={v.description} maxLength={300} onChange={(e) => set({ description: e.target.value })} />
        </Field>
      </div>
    </Modal>
  )
}

function MovesModal({ row, onClose }: { row: AdvanceBalance; onClose: () => void }) {
  const { t } = useTranslation()
  const q = useAdvanceMoves(row.userId)
  return (
    <Modal open size="lg" onOpenChange={(o) => !o && onClose()} icon={<History />} title={t('finance.staffAdvances.movesTitle', { name: row.userName })} description={t('finance.staffAdvances.balanceIs', { amount: fmtMoney(row.balance) })}>
      {q.isLoading ? (
        <Skeleton className="h-40" />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>{t('finance.staffAdvances.date')}</TH>
              <TH>{t('finance.staffAdvances.kind')}</TH>
              <TH>{t('finance.staffAdvances.description')}</TH>
              <TH className="text-right">{t('finance.staffAdvances.amount')}</TH>
            </TR>
          </THead>
          <TBody>
            {(q.data ?? []).map((m) => (
              <TR key={m.id}>
                <TD className="whitespace-nowrap tabular-nums">{fmtDate(m.date)}</TD>
                <TD>
                  <Badge tone={MOVE_TONE[m.kind]}>{t(`finance.staffAdvances.kinds.${m.kind}`)}</Badge>
                </TD>
                <TD className="max-w-80 truncate">{m.description}</TD>
                <TD className="text-right tabular-nums">{(m.kind === 'give' ? '+' : '−') + fmtMoney(m.amount)}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Modal>
  )
}
